import { z } from 'zod';

/**
 * Top-level power state of a machine, surfaced on the admin dashboard as the
 * "Power" column. Orthogonal to `lastKnownState` (host presence: idle/hosting/…).
 *
 * - `awake`   — the machine is on and reporting.
 * - `asleep`  — a graceful suspend/hibernate signal was received.
 * - `off`     — a graceful shutdown signal was received.
 * - `unknown` — looked awake, then went silent with no transition signal
 *               (power loss / crash / network loss). NOT written by events —
 *               derived at query time by realtime-api.
 */
export type PowerState = 'awake' | 'asleep' | 'off' | 'unknown';

/**
 * Granular power sub-state for display. `PBT_APMSUSPEND` fires for S3/S0/S4
 * without saying which, so the sleep sub-states are best-effort classifications
 * derived from static capability (AoAc) and hibernate configuration.
 */
export type PowerSubState =
  | 'awake'
  | 'sleep-s3' // legacy standby (non-AoAc)
  | 'modern-standby-s0' // AoAc / connected standby
  | 'hibernate-s4' // hibernation (best-effort classification)
  | 'shutdown-s5' // graceful power-off
  | 'unreachable'; // derived at query time (was awake, went silent, no signal)

export const PowerStateSchema = z.enum(['awake', 'asleep', 'off', 'unknown']);

export const PowerSubStateSchema = z.enum([
  'awake',
  'sleep-s3',
  'modern-standby-s0',
  'hibernate-s4',
  'shutdown-s5',
  'unreachable',
]);
